import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Card, CardContent, CardHeader, Chip } from "@material-ui/core";
import KanbanCard from "../KanbanCard";

const parseColorToRgb = (color) => {
  if (!color || typeof color !== "string") {
    return null;
  }

  const normalized = color.trim();

  if (normalized.startsWith("#")) {
    const hex = normalized.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      if ([r, g, b].some(Number.isNaN)) return null;
      return { r, g, b };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].some(Number.isNaN)) return null;
      return { r, g, b };
    }
  }

  const rgbMatch = normalized.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    if ([r, g, b].some(Number.isNaN)) return null;
    return { r, g, b };
  }

  return null;
};

const getReadableTextColor = (backgroundColor) => {
  const rgb = parseColorToRgb(backgroundColor);
  if (!rgb) {
    return null;
  }

  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness >= 160 ? "#111111" : "#FFFFFF";
};

const useStyles = makeStyles(theme => ({
  laneContainer: {
    display: "flex",
    flexDirection: "column",
    width: 320,
    minWidth: 320,
    maxWidth: 320,
    flexShrink: 0,
    minHeight: 500,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    boxShadow: theme.shadows[1],
    transition: "box-shadow 0.2s ease-in-out",
    "&:hover": {
      boxShadow: theme.shadows[3],
    },
  },
  laneHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: "bold",
  },
  laneHeaderOffset: {
    paddingTop: theme.spacing(3),
  },
  laneTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    textTransform: "uppercase",
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  laneBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    overflowY: "auto",
    flex: 1,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: theme.palette.action.active,
      },
    },
  },
  cardWrapper: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(0.5),
    transition: "all 0.15s ease-in-out",
    cursor: "move",
    "&:hover": {
      boxShadow: theme.shadows[2],
      transform: "translateY(-2px)",
    },
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.text.secondary,
    fontSize: "0.9em",
  },
}));

/**
 * KanbanLane - Memoized lane component for optimal rendering
 * Only re-renders when cards for this specific lane change
 */
const KanbanLane = React.memo(({
  laneId,
  title,
  cards,
  backgroundColor,
  onCardClick,
  onCardDragStart,
  onCardDragOver,
  onCardDrop,
}) => {
  const classes = useStyles();
  const laneTextColor = getReadableTextColor(backgroundColor);
  const chipBackgroundColor = laneTextColor === "#111111"
    ? "rgba(255,255,255,0.62)"
    : "rgba(0,0,0,0.30)";

  const handleDragStart = (e, cardId) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("cardId", cardId);
    e.dataTransfer.setData("sourceLaneId", laneId);
    onCardDragStart?.(cardId, laneId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    onCardDragOver?.(e, laneId);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const cardId = e.dataTransfer.getData("cardId");
    const sourceLaneId = e.dataTransfer.getData("sourceLaneId");
    onCardDrop?.(cardId, sourceLaneId, laneId);
  };

  return (
    <div
      className={classes.laneContainer}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div
        className={clsx(classes.laneHeader, classes.laneHeaderOffset)}
        style={backgroundColor ? { backgroundColor, color: laneTextColor || undefined } : {}}
      >
        <span
          className={classes.laneTitle}
          style={backgroundColor ? { color: laneTextColor || undefined } : {}}
        >
          {title}
        </span>
        <Chip
          size="small"
          label={cards.length}
          color="default"
          style={{
            backgroundColor: backgroundColor ? chipBackgroundColor : "rgba(255,255,255,0.3)",
            color: laneTextColor || "white",
            fontWeight: 700,
          }}
        />
      </div>
      <div className={classes.laneBody}>
        {cards.length === 0 ? (
          <div className={classes.emptyState}>No items</div>
        ) : (
          cards.map((card) => (
            <div
              key={card.id}
              className={classes.cardWrapper}
              draggable
              onDragStart={(e) => handleDragStart(e, card.id)}
            >
              <KanbanCard
                ticket={card}
                onCardClick={onCardClick}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if cards actually changed
  // This is a shallow comparison optimized for performance
  return (
    prevProps.laneId === nextProps.laneId &&
    prevProps.title === nextProps.title &&
    prevProps.cards.length === nextProps.cards.length &&
    prevProps.cards.every((card, idx) =>
      card.id === nextProps.cards[idx]?.id &&
      card.updatedAt === nextProps.cards[idx]?.updatedAt &&
      card.unreadMessages === nextProps.cards[idx]?.unreadMessages
    ) &&
    prevProps.backgroundColor === nextProps.backgroundColor &&
    prevProps.onCardClick === nextProps.onCardClick
  );
});

KanbanLane.displayName = "KanbanLane";

export default KanbanLane;
